// CmFormSchemaFields — renders the capture inputs for a form-bound CmTable's
// `row_data` fields inside EntityForm. Looks up the published form for the
// table slug and maps its `schema.fields[]` through the shared `SchemaFieldInput`.
//
// Only `row_data.*` data fields render here: `name`/`description` are EntityForm's
// own inputs, and media (photo/video/audio) is handled by EntityForm's media flow.

import { useMemo } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { usePublishedFormsForTable } from "../../../api/fieldForms";
import { FieldForm, SchemaField } from "../../../types/fieldForm";
import { SchemaFieldInput } from "./SchemaFieldInput";

/** Media widgets — captured via EntityForm's existing media upload, not shown here as plain fields. */
const MEDIA_WIDGETS = new Set(["photo", "video", "audio"]);

interface Props {
  /** Bound CmTable slug — used to look up the published form + its schema. */
  tableSlug: string;
  /** The row's `row_data` (EntityForm state). */
  rowData: Record<string, any>;
  /** Called with the next `row_data` object (matches EntityForm's `setRowData`). */
  onChange: (next: Record<string, any>) => void;
  disabled?: boolean;
}

/** `target` → the `row_data` key, or null for name/description/display targets. */
function rowDataKey(target: string | null | undefined): string | null {
  return typeof target === "string" && target.startsWith("row_data.") ? target.slice("row_data.".length) : null;
}

const CmFormSchemaFields: React.FC<Props> = ({ tableSlug, rowData, onChange, disabled }) => {
  const { data, isLoading } = usePublishedFormsForTable(tableSlug || null);

  // Latest published form bound to this table.
  const form = useMemo<FieldForm | null>(() => {
    const list = data?.results ?? [];
    return list.reduce<FieldForm | null>((latest, f) => (!latest || f.version > latest.version ? f : latest), null);
  }, [data]);

  // Capturable row_data fields (skip display + media + name/description targets).
  const fields = useMemo<SchemaField[]>(
    () => (form?.schema?.fields ?? []).filter((f) => f.kind !== "display" && !MEDIA_WIDGETS.has(f.widget) && rowDataKey(f.target) != null),
    [form]
  );

  if (!tableSlug) return null;

  if (isLoading && !form) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant='body2' color='text.secondary'>
          Loading form fields…
        </Typography>
      </Box>
    );
  }

  if (fields.length === 0) return null;

  const setField = (key: string, value: unknown) => onChange({ ...rowData, [key]: value });

  return (
    <Box sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Typography variant='body1' color='grey.600' sx={{ mb: 1 }}>
        Form fields
      </Typography>
      <Stack spacing={2}>
        {fields.map((f) => {
          const key = rowDataKey(f.target) as string;
          return <SchemaFieldInput key={f.key} field={f} value={rowData[key]} onChange={(v) => setField(key, v)} />;
        })}
      </Stack>
    </Box>
  );
};

export default CmFormSchemaFields;
